import type { DragEndEvent } from '@dnd-kit/core';
import { CheckSquare, Plus, Search, Settings } from 'lucide-react';
import * as React from 'react';
import type { Task, TaskStatusMeta, WorkspaceMember } from '../../lib/task-list';
import { Badge } from '../../ui/badge';
import { EmptyState } from '../empty-state';
import { type ColumnData, type ItemData, KanbanBoard } from '../kanban/KanbanBoard';
import { TaskCard } from './TaskCard';

interface KanbanViewProps {
  tasks: Task[];
  statuses: TaskStatusMeta[];
  isLoading?: boolean;
  isReadOnly?: boolean;
  workspaceMembers: WorkspaceMember[];
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  onCreateTask?: () => void;
  onTaskClick: (taskId: number) => void;
  onStatusChange: (taskId: number, statusKey: string) => void;
}

/** Kanban board of tasks columned by workspace status. Fully generic — no per-app coupling. */
export function KanbanView({
  tasks,
  statuses,
  isLoading,
  isReadOnly,
  workspaceMembers,
  hasActiveFilters,
  onClearFilters,
  onCreateTask,
  onTaskClick,
  onStatusChange,
}: KanbanViewProps) {
  const [optimisticTasks, setOptimisticTasks] = React.useState<Task[]>(tasks);

  // Synchronize optimistic tasks whenever the incoming tasks prop changes (e.g. from server refetch)
  React.useEffect(() => {
    setOptimisticTasks(tasks);
  }, [tasks]);

  const columns: ColumnData[] = React.useMemo(
    () =>
      statuses.map((s) => {
        const count = optimisticTasks.filter((t) => (t.status || 'todo') === s.key).length;
        return {
          id: s.key,
          title: (
            <div className="flex items-center gap-2">
              <span
                className="h-2 w-2 rounded-full shrink-0"
                style={{ backgroundColor: s.color }}
              />
              <span className="font-bold text-foreground text-[12.5px]">{s.label}</span>
              <Badge
                variant="secondary"
                className="rounded-full bg-secondary/60 text-muted-foreground text-[10.5px] font-semibold px-1.5 py-0"
              >
                {count}
              </Badge>
            </div>
          ),
        };
      }),
    [statuses, optimisticTasks]
  );

  const items: ItemData[] = React.useMemo(
    () =>
      optimisticTasks.map((task) => ({
        id: task.id,
        columnId: task.status || 'todo',
        content: (
          <TaskCard
            task={task}
            statuses={statuses}
            members={workspaceMembers}
            onClick={() => onTaskClick(task.id)}
            showStatus={false}
            accentTop
          />
        ),
      })),
    [optimisticTasks, statuses, workspaceMembers, onTaskClick]
  );

  const handleDragEnd = (event: DragEndEvent) => {
    if (isReadOnly) return;
    const { active, over } = event;
    if (!over) return;

    const activeTask = optimisticTasks.find((t) => t.id === active.id);
    if (!activeTask) return;

    const overColumnId =
      ((over.data?.current as Record<string, unknown> | undefined)?.columnId as string) ||
      (() => {
        const overTask = optimisticTasks.find((t) => t.id === over.id);
        return overTask ? overTask.status || 'todo' : String(over.id);
      })();

    const currentStatusKey = activeTask.status || 'todo';
    if (overColumnId && overColumnId !== currentStatusKey) {
      // 1. Instantly update local state optimistically so UI never waits for server response
      setOptimisticTasks((prev) =>
        prev.map((t) => (t.id === activeTask.id ? { ...t, status: overColumnId } : t))
      );
      // 2. Fire mutation
      onStatusChange(activeTask.id, overColumnId);
    }
  };

  if (isLoading) {
    return (
      <div className="flex gap-4 h-full">
        {Array(3)
          .fill(0)
          .map((_, i) => (
            <div key={i} className="w-64 h-full rounded-xl bg-muted animate-pulse" />
          ))}
      </div>
    );
  }

  if (statuses.length === 0) {
    return (
      <EmptyState
        card
        icon={Settings}
        title="No statuses configured"
        description="Create task statuses to organize your workflow on the kanban board."
      />
    );
  }

  if (tasks.length === 0) {
    const isFiltered = hasActiveFilters ?? false;
    return (
      <EmptyState
        card
        icon={isFiltered ? Search : CheckSquare}
        title={isFiltered ? 'No tasks match your filters' : 'No tasks yet'}
        description={
          isFiltered
            ? 'Try relaxing your search terms or expanding your filter categories.'
            : 'Create tasks to organize them across status columns on this board.'
        }
        action={
          isFiltered && onClearFilters
            ? {
                label: 'Clear Filters',
                variant: 'outline',
                onClick: onClearFilters,
              }
            : !isReadOnly && onCreateTask
              ? {
                  label: 'Add Task',
                  icon: Plus,
                  onClick: onCreateTask,
                }
              : undefined
        }
      />
    );
  }

  return (
    <div className="h-full">
      <KanbanBoard columns={columns} items={items} onDragEnd={handleDragEnd} />
    </div>
  );
}
